import { UNITS, UNIT_CATEGORIES } from '../constants/units';

// Small utility: normalize terms for matching
function normalizeTerm(s) {
  // Replace common unit symbols with text equivalents before normalization
  const symReplaced = String(s)
    // preserve superscript 2/3 as digits so m² => m2 and doesn't collide with 'm'
    .replace(/\u00b2/g, '2')
    .replace(/\u00b3/g, '3')
    .replace(/\^2/g, '2')
    .replace(/\^3/g, '3')
    // degree symbols (° and º) -> 'degree'
    .replace(/[°º]/g, ' degree ')
    // prime symbols (′ ’ ') -> arcminute, double-prime (", ″) -> arcsecond
    .replace(/[′’']/g, ' arcmin ')
  .replace(new RegExp('[\u2033"]', 'g'), ' arcsec ');

  return symReplaced
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, ' ') // keep alphanumerics and spaces
    .replace(/\s+/g, ' ')
    .trim();
}

// Levenshtein distance for fuzzy matching
function levenshtein(a, b) {
  const an = a.length;
  const bn = b.length;
  if (an === 0) return bn;
  if (bn === 0) return an;
  const matrix = Array.from({ length: an + 1 }, () => Array(bn + 1).fill(0));
  for (let i = 0; i <= an; i++) matrix[i][0] = i;
  for (let j = 0; j <= bn; j++) matrix[0][j] = j;
  for (let i = 1; i <= an; i++) {
    for (let j = 1; j <= bn; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(matrix[i - 1][j] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j - 1] + cost);
    }
  }
  return matrix[an][bn];
}

// Build a map of possible aliases to unit keys and category
function buildAliasMap() {
  const map = {};

  const add = (key, names, category) => {
    names.forEach(n => {
      const k = normalizeTerm(n);
      if (k) map[k] = { unit: key, category };
    });
  };

  // For each category/unit, add the canonical name, symbol and some common variants
  Object.keys(UNITS).forEach(category => {
    Object.keys(UNITS[category]).forEach(unitKey => {
      const entry = UNITS[category][unitKey];
      const names = [];

      // canonical key split into words (e.g. squareMeter -> square meter)
      const split = unitKey.replace(/([A-Z])/g, ' $1').toLowerCase();
      names.push(split);

      // add symbol and common symbol forms
      if (entry.symbol) {
        names.push(entry.symbol);
        names.push(entry.symbol.toLowerCase());
        // keep superscripts as digits for alias matching (e.g. m² -> m2)
        names.push(entry.symbol.replace(/\u00b2/g, '2').replace(/\u00b3/g, '3'));
      }

      // common plural forms
      names.push(split + 's');

      // simple abbreviations: 'sq m' or 'm2'
      if (entry.symbol) {
        names.push(
          entry.symbol
            .toLowerCase()
            .replace(/\u00b2/g, '2')
            .replace(/\u00b3/g, '3')
        );
      }

      // friendly aliases for area
      if (category === UNIT_CATEGORIES.AREA) {
        names.push(split.replace('square ', 'sq '));
        names.push(split.replace('square ', 'sq. '));
        names.push(split.replace('square ', 'sq'));
      }

      if (category === UNIT_CATEGORIES.LENGTH) {
        names.push(split.replace('meter', 'metre'));
        names.push(split.replace('meter', 'metres'));
      }

      // normalize and add
      const unique = Array.from(new Set(names.filter(Boolean)));
      add(unitKey, unique, category);
    });
  });

  // Add a handful of hard-coded common aliases that aren't captured reliably
  const extras = {
    'sq ft': { unit: 'squareFoot', category: UNIT_CATEGORIES.AREA },
    sqft: { unit: 'squareFoot', category: UNIT_CATEGORIES.AREA },
    'square feet': { unit: 'squareFoot', category: UNIT_CATEGORIES.AREA },
    'square foot': { unit: 'squareFoot', category: UNIT_CATEGORIES.AREA },
    'ft^2': { unit: 'squareFoot', category: UNIT_CATEGORIES.AREA },
    ft2: { unit: 'squareFoot', category: UNIT_CATEGORIES.AREA },
    'ft²': { unit: 'squareFoot', category: UNIT_CATEGORIES.AREA },
    m2: { unit: 'squareMeter', category: UNIT_CATEGORIES.AREA },
    'm^2': { unit: 'squareMeter', category: UNIT_CATEGORIES.AREA },
    'm²': { unit: 'squareMeter', category: UNIT_CATEGORIES.AREA },
    'sq m': { unit: 'squareMeter', category: UNIT_CATEGORIES.AREA },
    sqm: { unit: 'squareMeter', category: UNIT_CATEGORIES.AREA },
    'square meters': { unit: 'squareMeter', category: UNIT_CATEGORIES.AREA },
    'square metres': { unit: 'squareMeter', category: UNIT_CATEGORIES.AREA },
    'sq ft.': { unit: 'squareFoot', category: UNIT_CATEGORIES.AREA },
    feet: { unit: 'foot', category: UNIT_CATEGORIES.LENGTH },
    foot: { unit: 'foot', category: UNIT_CATEGORIES.LENGTH },
    ft: { unit: 'foot', category: UNIT_CATEGORIES.LENGTH },
    meters: { unit: 'meter', category: UNIT_CATEGORIES.LENGTH },
    metres: { unit: 'meter', category: UNIT_CATEGORIES.LENGTH },
    m: { unit: 'meter', category: UNIT_CATEGORIES.LENGTH },
    km: { unit: 'kilometer', category: UNIT_CATEGORIES.LENGTH },
    'sq km': { unit: 'squareKilometer', category: UNIT_CATEGORIES.AREA },
    acre: { unit: 'acre', category: UNIT_CATEGORIES.AREA },
    acres: { unit: 'acre', category: UNIT_CATEGORIES.AREA },
    'square yard': { unit: 'squareYard', category: UNIT_CATEGORIES.AREA },
    'sq yd': { unit: 'squareYard', category: UNIT_CATEGORIES.AREA },
    degree: { unit: 'degree', category: UNIT_CATEGORIES.ANGLE },
    degrees: { unit: 'degree', category: UNIT_CATEGORIES.ANGLE },
    deg: { unit: 'degree', category: UNIT_CATEGORIES.ANGLE },
    '°': { unit: 'degree', category: UNIT_CATEGORIES.ANGLE },
    º: { unit: 'degree', category: UNIT_CATEGORIES.ANGLE },
    "'": { unit: 'arcminute', category: UNIT_CATEGORIES.ANGLE },
    '"': { unit: 'arcsecond', category: UNIT_CATEGORIES.ANGLE },
    arcmin: { unit: 'arcminute', category: UNIT_CATEGORIES.ANGLE },
    arcminute: { unit: 'arcminute', category: UNIT_CATEGORIES.ANGLE },
    arcminutes: { unit: 'arcminute', category: UNIT_CATEGORIES.ANGLE },
    arcsec: { unit: 'arcsecond', category: UNIT_CATEGORIES.ANGLE },
    arcsecond: { unit: 'arcsecond', category: UNIT_CATEGORIES.ANGLE },
    arcseconds: { unit: 'arcsecond', category: UNIT_CATEGORIES.ANGLE },
  };

  Object.keys(extras).forEach(k => (map[normalizeTerm(k)] = extras[k]));

  return map;
}

const ALIAS_MAP = buildAliasMap();
const ALIAS_KEYS = Object.keys(ALIAS_MAP);
// Precompute token counts for alias keys to prefer same-length matches
const ALIAS_KEY_TOKEN_COUNT = ALIAS_KEYS.reduce((acc, k) => {
  acc[k] = k.split(' ').length;
  return acc;
}, {});

function fuzzyLookup(term) {
  const n = normalizeTerm(term);
  if (!n) return null;
  if (ALIAS_MAP[n]) return ALIAS_MAP[n];

  // If term contains multiple tokens (spaces), avoid matching single-word alias keys by substring
  // (e.g. avoid matching 'm' inside 'm km'). Only allow substring match for single-token terms.
  const nTokens = n.split(' ').length;

  // First try: prefer keys that have the same token count (e.g. 'sq ft' matches 'sq ft', single-word matches single-word)
  for (const key of ALIAS_KEYS) {
    if (ALIAS_KEY_TOKEN_COUNT[key] !== nTokens) continue;
    if (key.includes(n) || n.includes(key)) return ALIAS_MAP[key];
  }

  // If single-token input, try other single-token keys by substring
  if (nTokens === 1) {
    for (const key of ALIAS_KEYS) {
      if (ALIAS_KEY_TOKEN_COUNT[key] !== 1) continue;
      if (key.includes(n) || n.includes(key)) return ALIAS_MAP[key];
    }
  }

  // Fallback: lowest levenshtein distance within threshold (stricter)
  let min = Infinity;
  let minKey = null;
  for (const key of ALIAS_KEYS) {
    // prefer keys with similar token counts when possible
    const tokenDiff = Math.abs(ALIAS_KEY_TOKEN_COUNT[key] - nTokens);
    const dist = levenshtein(n, key) + tokenDiff * 2; // penalize token count mismatch
    if (dist < min) {
      min = dist;
      minKey = key;
    }
  }

  // threshold: accept very small distances relative to length
  if (minKey && min <= Math.max(1, Math.floor(minKey.length * 0.2))) {
    return ALIAS_MAP[minKey];
  }

  return null;
}

/**
 * Try to parse a simple natural-language conversion query.
 * Returns { value, fromUnit, toUnit, category } or null if cannot parse.
 * Supports examples like:
 *  - "100 square feet is what square meters"
 *  - "convert 5.5 m2 to ft2"
 *  - "how many square meters in 100 sq ft?"
 */
export function parseConversionQuery(text) {
  if (!text || typeof text !== 'string') return null;
  // Pre-normalize common symbols (degree, arcminute, arcsecond) so tokenization finds them
  const raw = text.trim();
  const symbolNormalized = String(raw)
    // normalize superscripts to digits so 'm²' => 'm2'
    .replace(/\u00b2/g, '2')
    .replace(/\u00b3/g, '3')
    .replace(/[°º]/g, ' degree ')
    .replace(/[′’']/g, ' arcmin ')
    .replace(/[\u2033"]/g, ' arcsec ');
  const s = symbolNormalized.toLowerCase();

  // Find a number (allow decimals and scientific notation)
  // Prefer standalone numeric tokens (not part of words like km2)
  const numberRegex = /-?\d+(?:[.,]\d+)?(?:e[-+]?\d+)?/ig;
  let numberMatch = null;
  let m;
  const alnum = /[0-9a-z]/i;
  while ((m = numberRegex.exec(s)) !== null) {
    const idx = m.index;
    const len = m[0].length;
    const before = idx > 0 ? s[idx - 1] : null;
    const after = idx + len < s.length ? s[idx + len] : null;
    const beforeOk = before === null || !alnum.test(before);
    const afterOk = after === null || !alnum.test(after);
    if (beforeOk && afterOk) {
      numberMatch = m;
      break;
    }
    if (!numberMatch) numberMatch = m; // fallback to first match
  }
  if (!numberMatch) return null;
  let rawNum = numberMatch[0];
  rawNum = rawNum.replace(',', '.');
  const value = parseFloat(rawNum);
  if (isNaN(value)) return null;

  // Text surrounding the number
  const before = s.slice(0, numberMatch.index).trim();
  const after = s.slice(numberMatch.index + numberMatch[0].length).trim();

  // Tokenize but preserve common unit punctuation
  const tokenize = str =>
    (str || '')
      // convert superscripts to digits rather than dropping them
  .replace(/\u00b2/g, '2')
  .replace(/\u00b3/g, '3')
  .replace(/[^a-z0-9 ^.]+/g, ' ')
      .split(/\s+/)
      .filter(Boolean);

  let tokensAfter = tokenize(after);
  let tokensBefore = tokenize(before);

  // Remove common connector/stop words so unit tokens are easier to match
  const STOPWORDS = new Set([
    'to',
    'in',
    'into',
    'is',
    'what',
    'convert',
    'how',
    'many',
    'for',
    'from',
    'the',
    'a',
    'an',
  ]);
  tokensAfter = tokensAfter.filter(t => !STOPWORDS.has(t));
  tokensBefore = tokensBefore.filter(t => !STOPWORDS.has(t));

  // Try matching multi-word unit names from the right side (after) first
  let fromCandidate = null;
  let toCandidate = null;

  // Helper to join n tokens starting at i
  const joinTokens = (arr, i, n) => arr.slice(i, i + n).join(' ');

  // Search after tokens for a known unit (take up to 3 tokens)
  for (let len = 3; len >= 1; len--) {
    for (let i = 0; i + len <= tokensAfter.length; i++) {
      const t = joinTokens(tokensAfter, i, len);
      const found = fuzzyLookup(t);
      if (found) {
        fromCandidate = found;
        tokensAfter.splice(i, len);
        break;
      }
    }
    if (fromCandidate) break;
  }

  // If not found after, look before tokens (common: 'how many X in 100 Y')
  if (!fromCandidate) {
    for (let len = 3; len >= 1; len--) {
      for (let i = 0; i + len <= tokensBefore.length; i++) {
        const t = joinTokens(tokensBefore, i, len);
        const found = fuzzyLookup(t);
        if (found) {
          fromCandidate = found;
          tokensBefore.splice(i, len);
          break;
        }
      }
      if (fromCandidate) break;
    }
  }

  // Now try to find the target unit in remaining tokens (after or before)
  const remaining = tokensBefore.concat(tokensAfter);
  for (let len = 3; len >= 1; len--) {
    for (let i = 0; i + len <= remaining.length; i++) {
      const t = joinTokens(remaining, i, len);
      const found = fuzzyLookup(t);
      if (found) {
        toCandidate = found;
        break;
      }
    }
    if (toCandidate) break;
  }

  if (!fromCandidate || !toCandidate) return null;

  // If categories mismatch, bail (we don't support cross-category conversions)
  if (fromCandidate.category !== toCandidate.category) return null;

  return {
    value,
    fromUnit: fromCandidate.unit,
    toUnit: toCandidate.unit,
    category: fromCandidate.category,
  };
}
